import React, { useState } from 'react';
import {
  Alert,
  Checkbox,
  FormGroup,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  relationshipResetOptions,
  type RelationshipResetScope,
} from '../../../map-editor-features/editor/lib/resetRelationships';
import styles from '../../../map-editor-features/cctv-ip-import/ui/CctvIpImportButton.module.css';

import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema';

interface ResetRelationshipsControlProps {
  disabled: boolean;
  selectedCount: number;
  relationTypes?: Record<string, TemplateRelationType>;
  onReset: (scope: RelationshipResetScope, pairs?: string[]) => void;
}

export default function ResetRelationshipsControl({
  disabled,
  selectedCount,
  onReset,
  relationTypes = {},
}: ResetRelationshipsControlProps) {
  const options = relationshipResetOptions(relationTypes);
  const [checked, setChecked] = useState<string[]>([]);
  const [allKinds, setAllKinds] = useState(false);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<RelationshipResetScope>('selected');
  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => {
          setScope(selectedCount > 0 ? 'selected' : 'all');
          setChecked([]);
          setAllKinds(false);
          setOpen(true);
        }}
      >
        관계 초기화
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>객체 관계 초기화</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={scope}
            onChange={(_, value) => setScope(value as RelationshipResetScope)}
            aria-label="초기화 범위"
          >
            <FormControlLabel value="all" control={<Radio />} label="현재 층 전체 객체" />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              disabled={selectedCount === 0}
              label={`선택한 객체 ${selectedCount}개의 관계만 초기화`}
            />
          </RadioGroup>
          {selectedCount === 0 && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              특정 객체만 초기화하려면 창을 닫고 맵에서 객체를 먼저 선택하세요. 여러 객체도 선택할
              수 있습니다.
            </Typography>
          )}
          <Typography sx={{ mt: 2 }}>초기화할 관계 종류</Typography>
          <FormControlLabel
            control={<Checkbox checked={allKinds} onChange={(_, value) => setAllKinds(value)} />}
            label="모든 관계 종류"
          />
          <FormGroup>
            {options.map((option) => (
              <FormControlLabel
                key={option.key}
                label={option.label}
                control={
                  <Checkbox
                    checked={allKinds || checked.includes(option.key)}
                    disabled={allKinds}
                    onChange={(_, value) =>
                      setChecked((previous) =>
                        value
                          ? [...previous, option.key]
                          : previous.filter((key) => key !== option.key),
                      )
                    }
                  />
                }
              />
            ))}
          </FormGroup>
          <Alert severity="warning">
            체크한 관계 종류만 양방향으로 해제합니다. 체크하지 않은 관계는 유지됩니다.
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            객체의 위치·이름·IP는 유지됩니다. 실행 취소로 복구할 수 있으며, 맵 저장 후 반영됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>취소</Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              disabled ||
              (!allKinds && checked.length === 0) ||
              (scope === 'selected' && selectedCount === 0)
            }
            onClick={() => {
              onReset(scope, allKinds ? undefined : checked);
              setOpen(false);
            }}
          >
            관계 초기화 실행
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
